import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { RECURRING_ORDER_BUMPS_ENABLED, hasStock } from '@repo/lib';

import Button from '@dashboard/components/Button';
import ProductSelect from '@dashboard/components/Formik/components/ProductSelect';
import Reorderable from '@dashboard/components/Reorderable';
import { Separator } from '@dashboard/components/ui/separator';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { toast } from '@dashboard/context/ToastContext';
import usePage from '@dashboard/hooks/usePage';
import { hasRecurringPrice } from '@dashboard/services/getBillingModel';
import useProductQueries from '@dashboard/hooks/queries/useProductQueries';
import { confirm } from '@dashboard/lib/confirm';
import { pagesKey, productKey, sellerProductsKey } from '@dashboard/lib/queryKeys';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PAGE_TYPES } from '@dashboard/enums/page-types';

import BumpProductForm from './BumpProductForm';
import bumpUsageLabel from './bumpUsageLabel';
import { getOfferCopy } from './offerCopy';
import OrderBumpItem from './OrderBumpItem';
import useOrderBumpMutations, { useOrderBumps } from './useOrderBumpMutations';

// products[] entries carry an id that may arrive populated or raw depending on
// the page GET's projection.
const entryProductId = entry => String(entry?.productId?._id ?? entry?.productId ?? '');

function getPlacedProductIds(page, orderBumps) {
  const primaryId = page?.product?._id ?? page?.product;

  return [
    ...(primaryId ? [String(primaryId)] : []),
    ...(page?.products ?? []).map(entryProductId),
    ...orderBumps.map(entry => String(entry.productId)),
  ].filter(Boolean);
}

// The tickets a rule can watch: the ones the event sells, whatever group
// they sit in, with the group name kept so two "Standard" tickets stay apart.
function getTicketTypes(page, { includeDisabled = false } = {}) {
  return (page?.ticketGroups ?? [])
    .filter(group => includeDisabled || group?.status !== 'disabled')
    .flatMap(group =>
      (group.ticketTypes ?? [])
        .filter(ticketType => includeDisabled || ticketType?.status !== 'disabled')
        .map(ticketType => ({ ...ticketType, groupName: group.name })),
    );
}

const OrderBumpsForm = () => {
  const { page } = usePage();
  const { sellerId } = useAuth();
  const queryClient = useQueryClient();
  const pageId = page._id;

  const { orderBumps, isLoading, isError } = useOrderBumps(pageId);
  const { attach, update, detach, reorder } = useOrderBumpMutations(pageId);
  const {
    updateProductMutation,
    attachProductMedia,
    attachProductFile,
    copyProduct,
    getProduct,
    deleteProduct,
  } = useProductQueries();

  const [openId, setOpenId] = useState(null);
  const [addMode, setAddMode] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const isEvent = page?.type === PAGE_TYPES.EVENT;
  const copy = getOfferCopy(page);
  // An event prices its tickets in one currency and sells nothing recurring,
  // so an add-on follows the event where a bump follows the page product.
  const currency = (isEvent ? page.eventDetails?.currency : page?.product?.currency) ?? 'usd';
  const allowRecurring =
    RECURRING_ORDER_BUMPS_ENABLED && !isEvent && hasRecurringPrice(page?.product);
  // What a placement's conditional-logic rule points at: the buyer picks one of
  // these on the page, and the rule watches that choice. On a checkout that is
  // a price of the page product; on an event, the tickets in the basket.
  const primaryPrices = page?.product?.prices ?? [];
  const ticketTypes = isEvent ? getTicketTypes(page) : undefined;
  const allTicketTypes = isEvent ? getTicketTypes(page, { includeDisabled: true }) : undefined;

  const closeAdd = () => setAddMode(null);

  const handleAttach = async product => {
    if (!product?._id) return;

    try {
      await attach.mutateAsync(product._id);
      closeAdd();
    } catch {
      // The mutation's onError already surfaced the API's message.
    }
  };

  const handleCreate = async (values, attachments = {}, placement = {}) => {
    const { media = [], files = [] } = attachments;

    setIsCreating(true);

    let product;
    try {
      product = await updateProductMutation(values);
    } catch (error) {
      // Only the product create reports itself; the attach mutation has its own
      // onError, so the two steps must not share a catch or a failed attach
      // would toast twice.
      toast({ title: error?.message || 'Unable to create the product', type: 'error' });
      setIsCreating(false);
      return;
    }

    // The create form uploads its image and downloads before the product exists,
    // so the links are made here. A failure is reported but not fatal: the
    // product is already saved, and the row that opens below carries the same
    // two fields to retry in.
    try {
      for (const fileId of media) {
        await attachProductMedia(product._id, fileId);
      }

      for (const fileId of files) {
        await attachProductFile(product._id, fileId);
      }
    } catch (error) {
      toast({ title: error?.message || 'Unable to attach the uploaded files', type: 'error' });
    }

    try {
      await attach.mutateAsync({ productId: product?._id, placement });
      // Back to the list, collapsed: the create form now collects everything a
      // bump has, so there is nothing left to send the seller into.
      closeAdd();
    } catch {
      // The attach mutation's onError already surfaced the API's message.
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * A placement points at a shared product and a product can only be placed once
   * on a page, so duplicating a row copies the PRODUCT and attaches the copy —
   * unlike a price, where the duplicate lives inside the same product.
   *
   * The API does the copying: it clones the image and downloads into new file
   * records, so the two products can each drop a file without breaking the
   * other, and it leaves ids, Stripe bindings and the SKU behind.
   */
  const handleDuplicate = async entry => {
    setIsCreating(true);

    try {
      const title = entry.product?.title;
      const product = await copyProduct(entry.productId, title ? { title: `${title} (copy)` } : {});

      // The copy keeps how the original is offered on this page, rule included.
      await attach.mutateAsync({
        productId: product._id,
        placement: {
          enabled: entry.enabled !== false,
          preselected: entry.preselected === true,
          hidden: entry.hidden === true,
          allowQuantity: entry.allowQuantity === true,
          shortenDescription: entry.shortenDescription === true,
          ...(entry.showHideLogic && { showHideLogic: entry.showHideLogic }),
        },
      });
    } catch (error) {
      toast({ title: error?.message || `Unable to duplicate the ${copy.noun}`, type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Delete takes the product itself, unlike Remove which only unplaces it here.
   *
   * `fromAllPages` has the API take it off every page offering it before the
   * delete, which is what the warning promises. `usedOnPageCount` counts this
   * page too, hence the -1: what the seller needs warning about is the OTHER
   * pages that lose the offer.
   */
  const handleDeleteProduct = async entry => {
    const elsewhere = Math.max((entry.usedOnPageCount ?? 1) - 1, 0);

    const proceed = await confirm(`Delete ${copy.noun}?`, {
      description: elsewhere
        ? `This ${copy.noun} is used on ${elsewhere} other ${elsewhere === 1 ? 'page' : 'pages'}. Deleting it removes it from all of them.`
        : 'This deletes the product itself. It cannot be undone.',
      confirm: 'Delete',
    });

    if (!proceed) return;

    try {
      await deleteProduct(entry.productId, { fromAllPages: true });
    } catch (error) {
      toast({ title: error?.message || `Unable to delete the ${copy.noun}`, type: 'error' });
      return;
    }

    // Every page's list and billing model may have changed, not just this one.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: pagesKey }),
      queryClient.invalidateQueries({ queryKey: sellerProductsKey(sellerId) }),
    ]);
  };

  const handleRemove = async entry => {
    const proceed = await confirm(`Remove ${copy.noun}?`, {
      description: `Removes the ${copy.noun} from this page only.`,
      confirm: 'Remove',
    });
    if (!proceed) return;

    detach.mutate(entry.productId);
  };

  // The API demands an exact permutation, so every entry is draggable and the
  // payload is the whole list — unavailable placements included, since they are
  // still on the page and still hold their slot in the order.
  const handleReorder = reordered => {
    reorder.mutate(reordered.map(entry => entry.productId));
  };

  // Reorderable keys each Draggable off `_id`; a resolved entry has no `_id` of
  // its own, and productId is unique within a page's bumps.
  const reorderableItems = orderBumps.map(entry => ({ ...entry, _id: entry.productId }));

  const placedProductIds = getPlacedProductIds(page, orderBumps);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-gray-500 dark:text-muted-foreground">
        Unable to load {copy.plural}. Please try again.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {orderBumps.length > 0 && (
        <Reorderable
          id="order-bumps-list"
          className="border-t"
          items={reorderableItems}
          onReorder={handleReorder}
          renderItem={({ item: entry, dragHandleProps }) => (
            <OrderBumpItem
              pageId={pageId}
              entry={entry}
              dragHandleProps={dragHandleProps}
              isOpen={openId === entry.productId}
              onOpenChange={open => setOpenId(open ? entry.productId : null)}
              // The row's form fetches its product on open, so warm it as the
              // pointer arrives — a no-op once the product is cached.
              onPrefetch={() =>
                queryClient.prefetchQuery({
                  queryKey: productKey(entry.productId),
                  queryFn: () => getProduct(entry.productId),
                })
              }
              onToggleEnabled={() =>
                update.mutate({ productId: entry.productId, enabled: entry.enabled === false })
              }
              onDuplicate={() => handleDuplicate(entry)}
              onRemove={() => handleRemove(entry)}
              primaryPrices={primaryPrices}
              copy={copy}
              ticketTypes={ticketTypes}
              allTicketTypes={allTicketTypes}
              otherOrderBumps={orderBumps
                .filter(other => other.productId !== entry.productId)
                .map(other => ({ productId: other.productId, title: other.product?.title }))}
              // Preselect, Allow quantity, Shorten description, Hide and the
              // conditional rule all ride the row's Save and share this
              // endpoint, so they arrive as one patch.
              onPlacementSave={patch => update.mutate({ productId: entry.productId, ...patch })}
              onDelete={() => handleDeleteProduct(entry)}
              canDelete={!entry.hasPurchases}
              isSaving={update.isPending}
              isOpenDisabled={addMode !== null}
            />
          )}
        />
      )}

      {/* Editing a placement takes the panel over: adding another from under an
          open row is a second editor on the same list, which is a save-order
          question nobody wants. */}
      {addMode !== null && (
        <div className="space-y-4 rounded-md border p-4">
          <BumpProductForm
            currency={currency}
            allowRecurring={allowRecurring}
            copy={copy}
            primaryPrices={primaryPrices}
            ticketTypes={ticketTypes}
            allTicketTypes={allTicketTypes}
            otherOrderBumps={orderBumps.map(other => ({
              productId: other.productId,
              title: other.product?.title,
            }))}
            onCreate={handleCreate}
            onCancel={closeAdd}
            isSaving={isCreating}
          />
        </div>
      )}

      {addMode === null && openId === null && (
        <div className="space-y-4">
          {/* Reusing an existing bump product IS the default, so the picker
              stands on its own rather than behind a button; creating one is the
              alternative below it. */}
          {/* Keyed on what is already placed: picking here is an action, not a
              selection to hold, so `value` stays null and the input would keep
              the title it was given. A new placement remounts it empty. */}
          <ProductSelect
            key={placedProductIds.join(',')}
            value={null}
            onChange={handleAttach}
            placedIds={placedProductIds}
            // The API refuses a bump in another currency, or a recurring one while
            // the switch is off, so it is shown but cannot be picked, rather than
            // failing after the click.
            getUnavailableReason={product => {
              if (product.currency && product.currency !== currency) {
                return `Priced in ${product.currency.toUpperCase()}`;
              }
              if (!RECURRING_ORDER_BUMPS_ENABLED && hasRecurringPrice(product))
                return 'Recurring price';
              return null;
            }}
            placeholder={`Search existing ${copy.plural}`}
            emptyMessage={`No ${copy.plural} found`}
            role="orderbump"
            renderItemMeta={product => (
              <>
                {!hasStock(product) && <div>Sold out</div>}
                <div>{bumpUsageLabel(product.usedOnPageCount)}</div>
              </>
            )}
          />

          {/* shadcn's own "or continue with" divider: the rule sits behind the
              label, which hides the part it covers with the panel's background. */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">or</span>
            </div>
          </div>

          <div className="flex justify-center">
            <Button type="button" appearance="primary" onClick={() => setAddMode('new')}>
              <Plus className="size-4" />
              Add {copy.noun}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderBumpsForm;
